/**
 * DeleteAccountSection Component
 *
 * Destructive action section for the Account tab in Settings.
 * Requires email confirmation + password entry before deletion.
 */

import { useState } from 'react';
import { useAuth } from '@/features/auth/hooks/use-auth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useHouseholdMembers } from '@/shared/hooks/use-household-members';
import { toast } from '@/shared/hooks/use-toast';
import { useDeleteMyAccount } from '@/shared/hooks/use-user';

export function DeleteAccountSection() {
  const { user, logout } = useAuth();
  const { data: members } = useHouseholdMembers();
  const deleteAccount = useDeleteMyAccount();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState('');
  const [password, setPassword] = useState('');

  const isSoleMember = members?.length === 1;
  const emailMatches = emailConfirm === user?.email;
  const canConfirm = emailMatches && password.length > 0 && !deleteAccount.isPending;

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await deleteAccount.mutateAsync(password);
      toast({ title: 'Your account has been deleted.' });
      await logout();
    } catch (error) {
      toast({
        title: 'Failed to delete account',
        description: error instanceof Error ? error.message : 'Please check your password and try again.',
        variant: 'destructive',
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEmailConfirm('');
      setPassword('');
    }
  };

  return (
    <div className="space-y-4 pt-2">
      <div>
        <h3 className="text-sm font-medium text-destructive">Delete Account</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Permanently delete your account and all associated data. This action
          cannot be undone.
        </p>
      </div>

      {isSoleMember && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
          <p className="text-sm text-destructive">
            You are the only member of your household. Deleting your account will
            also permanently delete the entire household, including all categories,
            budgets, and shared data.
          </p>
        </div>
      )}

      <Button
        variant="destructive"
        size="sm"
        onClick={() => setDialogOpen(true)}
      >
        Delete My Account
      </Button>

      <AlertDialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your account, all transactions, bank
              connections, receipts, and other personal data. This action cannot
              be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            <Input
              label={`Type your email (${user?.email}) to confirm`}
              type="email"
              placeholder={user?.email}
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              autoComplete="off"
            />
            <Input
              label="Enter your password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAccount.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              disabled={!canConfirm}
              onClick={handleDelete}
            >
              {deleteAccount.isPending ? 'Deleting...' : 'Delete Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
